import React from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Progress, 
  Space, 
  Typography, 
  Tag,
  Tooltip,
  Divider
} from 'antd';
import { 
  CheckCircleOutlined, 
  ExclamationCircleOutlined, 
  WarningOutlined,
  InfoCircleOutlined,
  CalendarOutlined,
  ToolOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;

const InventoryStatusSummary = ({ 
  dateRange, 
  inventoryStats, 
  overlappingReservations = [],
  loading = false 
}) => {
  // Calculate overall statistics
  const calculateOverallStats = () => {
    if (!inventoryStats.byMaterialType) {
      return {
        totalItems: 0,
        availableItems: 0,
        reservedItems: 0,
        unavailableItems: 0,
        utilizationPercentage: 0
      };
    }

    let totalItems = 0;
    let availableItems = 0;
    let reservedItems = 0;
    let unavailableItems = 0;

    // Calculate reserved quantities from overlapping reservations
    const reservedQuantities = {};
    overlappingReservations.forEach(reservation => {
      if (reservation.reservedItems) {
        reservation.reservedItems.forEach(item => {
          reservedQuantities[item.materialType] = 
            (reservedQuantities[item.materialType] || 0) + (item.quantity || 0);
        });
      }
    });

    // Calculate totals across all material types
    Object.entries(inventoryStats.byMaterialType).forEach(([materialType, stats]) => {
      const total = stats.totalQuantity || 0;
      const damaged = stats.damagedQuantity || 0;
      const usable = (stats.newQuantity || 0) + (stats.usedQuantity || 0);
      const reserved = reservedQuantities[materialType] || 0;
      const available = Math.max(0, usable - reserved);

      totalItems += total;
      availableItems += available;
      reservedItems += Math.min(reserved, usable); // Can't reserve more than usable
      unavailableItems += damaged;
    });

    const utilizationPercentage = totalItems > 0 
      ? Math.round(((reservedItems + unavailableItems) / totalItems) * 100)
      : 0;

    return {
      totalItems,
      availableItems,
      reservedItems,
      unavailableItems,
      utilizationPercentage
    };
  };

  const stats = calculateOverallStats();

  // Render status card
  const renderStatusCard = (title, value, icon, color, description) => (
    <Card size="small" className="status-card">
      <Statistic
        title={
          <Space>
            {icon}
            <Text strong>{title}</Text>
          </Space>
        }
        value={value}
        valueStyle={{ color }}
        suffix={
          <Tooltip title={description}>
            <InfoCircleOutlined style={{ fontSize: '12px', color: '#8c8c8c' }} />
          </Tooltip>
        }
      />
    </Card>
  );

  // Render utilization progress
  const renderUtilizationProgress = () => {
    const availablePercent = stats.totalItems > 0 
      ? Math.round((stats.availableItems / stats.totalItems) * 100) 
      : 0;
    const reservedPercent = stats.totalItems > 0 
      ? Math.round((stats.reservedItems / stats.totalItems) * 100) 
      : 0;
    const unavailablePercent = stats.totalItems > 0 
      ? Math.round((stats.unavailableItems / stats.totalItems) * 100) 
      : 0;

    return (
      <div className="utilization-progress">
        <div className="progress-header">
          <Text strong>Inventory Utilization</Text>
          <Text type="secondary">
            {dateRange ? `${dateRange[0].format('MMM DD')} - ${dateRange[1].format('MMM DD')}` : 'No date range selected'}
          </Text>
        </div>
        
        <div className="progress-bars" style={{ marginTop: 16 }}>
          <div className="progress-item">
            <div className="progress-label">
              <Space>
                <CheckCircleOutlined style={{ color: '#52c41a' }} />
                <Text>Available</Text>
                <Tag color="success">{stats.availableItems} items</Tag>
              </Space>
              <Text type="secondary">{availablePercent}%</Text>
            </div>
            <Progress 
              percent={availablePercent} 
              strokeColor="#52c41a" 
              showInfo={false}
              size="small"
            />
          </div>

          <div className="progress-item" style={{ marginTop: 8 }}>
            <div className="progress-label">
              <Space>
                <CalendarOutlined style={{ color: '#faad14' }} />
                <Text>Reserved</Text>
                <Tag color="warning">{stats.reservedItems} items</Tag>
              </Space>
              <Text type="secondary">{reservedPercent}%</Text>
            </div>
            <Progress 
              percent={reservedPercent} 
              strokeColor="#faad14" 
              showInfo={false}
              size="small"
            />
          </div>

          <div className="progress-item" style={{ marginTop: 8 }}>
            <div className="progress-label">
              <Space>
                <ToolOutlined style={{ color: '#ff4d4f' }} />
                <Text>Unavailable</Text>
                <Tag color="error">{stats.unavailableItems} items</Tag>
              </Space>
              <Text type="secondary">{unavailablePercent}%</Text>
            </div>
            <Progress 
              percent={unavailablePercent} 
              strokeColor="#ff4d4f" 
              showInfo={false}
              size="small"
            />
          </div>
        </div>
      </div>
    );
  };

  // Render reservation details
  const renderReservationDetails = () => {
    if (!dateRange || overlappingReservations.length === 0) {
      return null;
    }

    return (
      <Card 
        title={
          <Space>
            <CalendarOutlined />
            <Text>Active Reservations</Text>
            <Tag color="processing">{overlappingReservations.length}</Tag>
          </Space>
        }
        size="small" 
        className="reservation-details-card"
      >
        <div className="reservation-list">
          {overlappingReservations.slice(0, 5).map((reservation, index) => (
            <div key={reservation.id || index} className="reservation-item">
              <div className="reservation-header">
                <Text strong>{reservation.invoiceNumber || `Reservation ${index + 1}`}</Text>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {reservation.startDate} to {reservation.endDate}
                </Text>
              </div>
              <div className="reservation-materials">
                {reservation.reservedItems?.slice(0, 3).map((item, itemIndex) => (
                  <Tag key={itemIndex} size="small" color="blue">
                    {item.quantity} {item.materialType}
                  </Tag>
                ))}
                {reservation.reservedItems?.length > 3 && (
                  <Tag size="small" color="default">
                    +{reservation.reservedItems.length - 3} more
                  </Tag>
                )}
              </div>
            </div>
          ))}
          
          {overlappingReservations.length > 5 && (
            <div className="reservation-item">
              <Text type="secondary" style={{ fontStyle: 'italic' }}>
                +{overlappingReservations.length - 5} more reservations...
              </Text>
            </div>
          )}
        </div>
      </Card>
    );
  };

  if (!dateRange) {
    return (
      <Card className="inventory-status-summary">
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <CalendarOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
          <Title level={4} type="secondary">Select Date Range</Title>
          <Text type="secondary">
            Choose a date range to view detailed inventory status including reserved and unavailable items.
          </Text>
        </div>
      </Card>
    );
  }

  return (
    <div className="inventory-status-summary">
      {/* Overall Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={6}>
          {renderStatusCard(
            'Total Items',
            stats.totalItems,
            <InfoCircleOutlined />,
            '#1890ff',
            'Total number of items in inventory'
          )}
        </Col>
        <Col xs={24} sm={12} md={6}>
          {renderStatusCard(
            'Available',
            stats.availableItems,
            <CheckCircleOutlined />,
            '#52c41a',
            'Items available for rental during selected period'
          )}
        </Col>
        <Col xs={24} sm={12} md={6}>
          {renderStatusCard(
            'Reserved',
            stats.reservedItems,
            <CalendarOutlined />,
            '#faad14',
            'Items already booked for overlapping dates'
          )}
        </Col>
        <Col xs={24} sm={12} md={6}>
          {renderStatusCard(
            'Unavailable',
            stats.unavailableItems,
            <ToolOutlined />,
            '#ff4d4f',
            'Damaged or unusable items'
          )}
        </Col>
      </Row>

      {/* Utilization Progress */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card loading={loading} className="utilization-card">
            {renderUtilizationProgress()}
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          {renderReservationDetails()}
        </Col>
      </Row>
    </div>
  );
};

export default InventoryStatusSummary;
